import { Controller } from "@hotwired/stimulus"

// « Finaliser le paiement » of a subscription line (card 12): the Payment Element, in deferred mode
// so it offers card and Link only, confirms the first invoice's own PaymentIntent (the card on file
// or a new one). Stripe then sends the buyer back to the page, which reads the invoice again.
// The page is a GET and never writes to Stripe: before mounting, the controller asks the server
// (POST) to have the intent save the card paid here, and mounts with the future usage it answers.
// No optional chaining: the webpack/Babel setup does not transpile it.
export default class extends Controller {
  static values = {
    publishableKey: String,
    clientSecret: String,
    amountCents: Number,
    paymentMethodTypes: Array,
    setupFutureUsage: String,
    cardSavingUrl: String,
    country: String,
    returnUrl: String,
    genericError: String
  }

  static targets = ["payment", "errors", "submit"]

  connect() {
    this.paying = false
    // Stripe.js blocked (an ad blocker, a network failure): the button stays disabled.
    // On load: told (role=alert), the page neither scrolled nor its focus taken.
    if (typeof Stripe === "undefined") return this.showError(this.genericErrorValue, false)

    this.stripe = Stripe(this.publishableKeyValue)
    this.saveCard().then((setupFutureUsage) => this.mount(setupFutureUsage))

    // Back from Stripe's redirect page restores this one from the browser cache, « paying » set.
    this.restored = (event) => { if (event.persisted) this.release("") }
    window.addEventListener("pageshow", this.restored)
  }

  // The intent's future usage once the server asked Stripe to save the card; the one the page
  // rendered when that answer does not come (the element must match the intent).
  async saveCard() {
    try {
      const token = document.querySelector("meta[name='csrf-token']")
      const response = await fetch(this.cardSavingUrlValue, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": token ? token.content : "" },
        credentials: "same-origin"
      })
      if (!response.ok) return this.setupFutureUsageValue

      const body = await response.json()
      return body.setup_future_usage
    } catch (err) {
      return this.setupFutureUsageValue
    }
  }

  mount(setupFutureUsage) {
    // 16px on a phone (a smaller input makes iOS Safari zoom on focus), 14.5px on a desktop. 768px,
    // as the cart: at 640px an iPhone in landscape (667-932px) would count as a desktop.
    const fontSize = window.matchMedia("(max-width: 768px)").matches ? "16px" : "14.5px"
    // Must match the invoice's PaymentIntent: amount, currency, its future usage.
    const options = {
      mode: "payment",
      amount: this.amountCentsValue,
      currency: "eur",
      paymentMethodTypes: this.paymentMethodTypesValue,
      locale: "fr",
      appearance: {
        theme: "flat",
        variables: { fontFamily: "'DM Sans', sans-serif", fontSizeBase: fontSize, borderRadius: "10px", colorText: "#281A39" },
        rules: { ".Input": { fontSize: fontSize } }
      }
    }
    if (setupFutureUsage) options.setupFutureUsage = setupFutureUsage
    this.elements = this.stripe.elements(options)

    // Its country picker starts on the line's delivery country, never Stripe's guess from the IP.
    const payment = this.elements.create("payment", {
      terms: { card: "never" },
      defaultValues: { billingDetails: { address: { country: this.countryValue || "FR" } } }
    })
    payment.mount(this.paymentTarget)
    payment.on("change", () => this.showError(""))
    payment.on("ready", () => { this.submitTarget.disabled = false })
  }

  disconnect() {
    if (this.restored) window.removeEventListener("pageshow", this.restored)
  }

  async pay() {
    if (this.paying || !this.elements) return

    this.paying = true
    this.submitTarget.disabled = true
    this.showError("")
    try {
      const { error: submitError } = await this.elements.submit()
      if (submitError) return this.release(submitError.message)

      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        clientSecret: this.clientSecretValue,
        confirmParams: { return_url: this.returnUrlValue }
      })
      // Only reached when Stripe did not redirect (declined card, closed popup...).
      if (error) this.release(error.message)
    } catch (err) {
      this.release(this.genericErrorValue)
    }
  }

  release(message) {
    this.paying = false
    this.submitTarget.disabled = false
    this.showError(message)
  }

  // reveal: after the buyer's own action, as on the cart, the error is brought to the middle of the
  // screen and focused, so it is seen and read.
  showError(message, reveal = true) {
    this.errorsTarget.textContent = message || ""
    if (message && reveal) {
      this.errorsTarget.scrollIntoView({ block: "center" })
      this.errorsTarget.focus({ preventScroll: true })
    }
  }
}
